import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'

import { LinkButton } from '~/components/link-button'
import { getJwtToken } from '~/utils/get-jwt-token'
import { readPersistedLastVisitedHref } from '~/utils/last-visited-route'

export const PublicHomePage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const hasToken = Boolean(getJwtToken())

  useEffect(() => {
    if (!hasToken) return

    const restoredPathname = readPersistedLastVisitedHref()
    if (restoredPathname) {
      void navigate({ href: restoredPathname, replace: true })
      return
    }

    void navigate({ to: '/list', replace: true })
  }, [hasToken, navigate])

  if (hasToken) return null

  return (
    <article
      className="flex h-screen w-screen flex-col items-center justify-center px-4 pb-12 pt-10 sm:px-6 sm:pt-14 md:pt-16 lg:px-8"
      data-testid="public-home"
    >
      <div className="flex w-full max-w-xl flex-col items-center text-center lg:max-w-2xl">
        <img
          alt="lingo cards logo"
          className="mb-8 h-16 w-16 sm:mb-10 sm:h-20 sm:w-20"
          decoding="async"
          height={80}
          src="/favicon.svg"
          width={80}
          aria-hidden
        />
        <h1 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
          {t('Lingo Cards')}
        </h1>
        <p className="mt-4 max-w-prose text-pretty text-base leading-relaxed text-muted-foreground sm:mt-5 sm:text-lg md:text-xl">
          {t('Master new words effortlessly')}
        </p>
        <div className="mt-10 flex w-full max-w-sm flex-col items-stretch gap-3 sm:mt-12 sm:max-w-none sm:items-center">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4">
            <LinkButton className="w-full sm:w-auto sm:min-w-[10rem]" size="lg" to="/auth/login">
              {t('Login')}
            </LinkButton>
            <LinkButton className="w-full sm:w-auto sm:min-w-[10rem]" size="lg" to="/auth/register" variant="outline">
              {t('Sign up')}
            </LinkButton>
          </div>
          <LinkButton className="w-full sm:w-auto sm:min-w-[10rem]" size="lg" to="/demo/list" variant="outline">
            {t('Try demo')}
          </LinkButton>
        </div>
        <p className="mt-10 max-w-prose text-balance text-center text-sm text-muted-foreground sm:mt-12">
          <Trans
            components={{
              privacyLink: (
                <Link className="font-medium text-foreground underline underline-offset-4" to="/privacy-policy" />
              ),
            }}
            i18nKey="By continuing, you agree to Lingo Cards Privacy Policy"
          />
        </p>
      </div>
    </article>
  )
}
